import { useState, type ReactNode } from "react";
import { Button } from "@vb/ui";
import { ChestOpening } from "./ChestOpening.js";

interface WinScreenProps {
  multiplier: number;
  payout: number;
  currency: string;
  stake: number;
  onDone: () => void;
}

interface BustScreenProps {
  multiplier: number;
  stake: number;
  currency: string;
  onDone: () => void;
}

/**
 * Centred modal panel, dark backdrop, ~720x540. Contains the ChestOpening
 * cinematic. Used by both Win and Bust outcomes — the only differences are
 * accent colour, the bottom-bar copy, and the cinematic palette.
 */
function EndModal({
  outcome,
  children,
  onBackdropClick,
}: {
  outcome: "win" | "bust";
  children: ReactNode;
  onBackdropClick?: () => void;
}) {
  const accent =
    outcome === "win"
      ? "rgba(212,162,76,0.45)"
      : "rgba(255,59,71,0.45)";
  const accentGlow =
    outcome === "win"
      ? "0 0 40px rgba(212,162,76,0.18)"
      : "0 0 40px rgba(255,59,71,0.18)";
  return (
    <div
      onClick={onBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(6,8,15,0.78)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          // Mobile fits via 95vw cap; aspectRatio still locks proportions but
          // maxHeight ensures the modal never exceeds the viewport.
          width: "min(720px, 95vw)",
          aspectRatio: "4 / 3",
          maxHeight: "min(calc(100vh - 48px), 90vh)",
          background: "var(--bg-1)",
          border: `1px solid ${accent}`,
          borderRadius: "var(--r-xl)",
          boxShadow: `var(--elev-3), ${accentGlow}`,
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// SceneWin/SceneBust own the in-cinematic banner ("CASHED OUT" / multiplier /
// BTC payout). EndScreens' bottom bar is reduced to the single CTA so it
// doesn't duplicate the banner text underneath itself.

export function WinScreen({ multiplier, stake, currency, onDone }: WinScreenProps) {
  const [cinematicDone, setCinematicDone] = useState(false);
  return (
    <EndModal outcome="win">
      <ChestOpening
        multiplier={multiplier}
        outcome="win"
        duration={7.2}
        stake={stake}
        currency={currency}
        onComplete={() => setCinematicDone(true)}
      />
      {cinematicDone && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 24,
            display: "flex",
            justifyContent: "center",
            zIndex: 60,
          }}
        >
          <Button
            onClick={onDone}
            variant="gold"
            style={{ padding: "12px 28px", fontSize: 13 }}
          >
            NEXT ROUND
          </Button>
        </div>
      )}
    </EndModal>
  );
}

export function BustScreen({ multiplier, stake, currency, onDone }: BustScreenProps) {
  const [cinematicDone, setCinematicDone] = useState(false);
  return (
    <EndModal outcome="bust">
      <ChestOpening
        multiplier={multiplier}
        outcome="bust"
        duration={6.0}
        stake={stake}
        currency={currency}
        onComplete={() => setCinematicDone(true)}
      />
      {cinematicDone && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 24,
            display: "flex",
            justifyContent: "center",
            zIndex: 60,
          }}
        >
          <Button
            onClick={onDone}
            style={{ padding: "12px 28px", fontSize: 13 }}
          >
            CONTINUE
          </Button>
        </div>
      )}
    </EndModal>
  );
}
